import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, User, ArrowRight, Users } from "lucide-react";
import axios from "axios";
import Brand from "../components/Brand";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  all: "All",
  apologist: "Apologists",
  wellness: "Wellness",
  bible_teacher: "Bible Teachers",
  homeschool: "Homeschool",
  marketplace: "Marketplace",
  ministry: "Ministry",
  coach: "Coaches",
  other: "Others",
};

/**
 * MemberDirectory — Mike's "search engine on the front page" idea.
 * Reusable component used both on the Landing page (compact featured strip)
 * and on a full /directory route.
 */
export default function MemberDirectory({ compact = false, max = 12 }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (cat !== "all") params.category = cat;
      params.limit = compact ? max : 60;
      const r = await axios.get(`${API_BASE}/directory`, { params });
      setMembers(r.data.members || []);
    } catch {
      setMembers([]);
    }
    setLoading(false);
  }, [q, cat, compact, max]);

  useEffect(() => {
    const timer = setTimeout(fetchMembers, q ? 250 : 0); // debounce search
    return () => clearTimeout(timer);
  }, [fetchMembers, q]);

  return (
    <div className={compact ? "space-y-4" : "space-y-6"} data-testid="member-directory">
      {/* Search bar */}
      <div className="rounded-2xl bg-gradient-to-br from-[#FFFFFF] to-[#EAF6FC] border-2 border-[#F39A1F]/50 p-5 md:p-6 shadow-xl">
        <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mb-2">★ FIND A <Brand/> MEMBER</div>
        <h2 className={`font-serif-display ${compact ? "text-2xl md:text-3xl" : "text-3xl md:text-5xl"} text-red-wine uppercase leading-tight`}>Search the Faithful.</h2>
        <p className="text-sm md:text-base text-foreground mt-1 max-w-2xl">Find a Christian coach, an apologist, a wellness teacher, a homeschool family, or a faith-driven marketplace — every member runs on <Brand suffix=".com"/>.</p>

        <div className="relative mt-4">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-foreground/50"/>
          <input
            type="search"
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="directory-search"
            placeholder="Try: wellness · author · Texas · Mike…"
            className="w-full bg-white border-2 border-foreground/15 focus:border-[#F39A1F] rounded-full px-12 py-3.5 text-base outline-none transition shadow"
          />
        </div>

        <div className="flex flex-wrap gap-1.5 mt-4">
          {Object.entries(CATEGORY_LABEL).map(([k, l]) => (
            <button
              key={k}
              onClick={() => setCat(k)}
              data-testid={`directory-cat-${k}`}
              className={`px-3 py-1.5 rounded-full text-[11px] tracking-wider-cap font-bold uppercase transition ${cat === k ? "bg-red-wine text-white shadow" : "bg-white text-foreground border border-foreground/15 hover:border-[#F39A1F]"}`}
            >{l}</button>
          ))}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-center py-10 text-foreground/60">Searching…</div>
      ) : members.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[#F39A1F]/40 bg-white/40 p-10 text-center" data-testid="directory-empty">
          <Users className="w-12 h-12 text-[#F39A1F] mx-auto mb-3"/>
          <div className="font-serif-display text-2xl text-red-wine">No members yet match that search.</div>
          <div className="text-foreground text-sm mt-1">The directory grows as new members publish their funnels. Be the first — <Link to="/signup?plan=free" className="text-[#2E9F3A] font-bold hover:underline">claim your free page</Link>.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-results">
          {members.map(m => (
            <Link
              key={m.id}
              to={`/u/${m.username}`}
              data-testid={`directory-member-${m.username}`}
              className="group rounded-xl border-2 border-foreground/10 hover:border-[#F39A1F] hover:shadow-2xl hover:-translate-y-0.5 bg-white overflow-hidden transition"
            >
              {/* Funnel hero or fallback */}
              <div className="relative h-40 bg-foreground/10">
                {m.funnel?.image
                  ? <img src={m.funnel.image} alt="" className="w-full h-full object-cover"/>
                  : <div className="w-full h-full bg-gradient-to-br from-[#FFFFFF] to-[#EAF6FC] flex items-center justify-center"><Users className="w-12 h-12 text-[#F39A1F]/50"/></div>}
                <div className="absolute top-2 left-2 bg-[#0F172A]/85 text-[#F39A1F] text-[9px] tracking-wider-cap font-bold uppercase px-2 py-0.5 rounded">
                  {CATEGORY_LABEL[m.profile_category] || "Member"}
                </div>
              </div>
              {/* Avatar overlap */}
              <div className="px-5 pb-5 pt-0 -mt-9 relative">
                <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-white bg-white flex items-center justify-center shadow-md">
                  {m.profile_photo
                    ? <img src={m.profile_photo} alt="" className="w-full h-full object-cover"/>
                    : <User className="w-8 h-8 text-foreground/40"/>}
                </div>
                <div className="font-bold text-red-wine text-base mt-2 leading-tight uppercase">{m.full_name}</div>
                {m.headline && <div className="text-[12px] text-[#D7800A] italic font-semibold mt-0.5 line-clamp-2">{m.headline}</div>}
                {m.location && <div className="text-[11px] text-foreground/85 mt-1.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{m.location}</div>}
                <div className="text-[10px] tracking-wider-cap font-bold text-foreground/60 mt-2"><Brand suffix=".com"/>/u/{m.username}</div>
                <div className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-wider-cap font-bold text-[#2E9F3A] uppercase group-hover:gap-2 transition-all">View Page <ArrowRight className="w-3 h-3"/></div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* "View all" CTA on compact mode */}
      {compact && members.length >= max && (
        <div className="text-center">
          <Link to="/directory" className="inline-flex items-center gap-2 text-[11px] tracking-wider-cap font-bold text-red-wine hover:text-[#2E9F3A] uppercase">Browse the full Directory <ArrowRight className="w-3 h-3"/></Link>
        </div>
      )}
    </div>
  );
}
